import type { PublicAdvisorProSummary } from '@/api/auth'
import City, { type CityData } from './city'
import DisplayUser from './displayUser'
import Membership from './membership'
import Post from './post'
import { TravellerType } from './travellerType'
import { TravelWithType } from './travelWithType'

export default class User {
  _id: string
  cognito_username: string
  name: string
  email: string
  bio?: string
  date_of_birth?: Date
  points: number
  large_avatar_url?: string
  small_avatar_url?: string
  avatar_url?: string
  profile_image_url?: string
  hero_image_url?: string
  heroImage?: string
  hero_image_secondary_url?: string
  secondary_hero_image_url?: string
  cover_image?: string
  city_identifying_name?: string
  traveller_types: TravellerType[] = []
  travel_with_types: TravelWithType[] = []
  city?: City
  dream_destinations: City[] = []
  dream_destination_reason?: string
  did_complete_onboarding: boolean
  membership?: Membership
  blocked_user_ids: string[]
  blocked_circle_user_ids: string[]
  is_deleted?: boolean
  is_hidden?: boolean
  is_banned?: boolean
  is_approved?: boolean
  is_featured?: boolean
  is_gt_plus?: boolean
  is_internal?: boolean
  hotel_margin?: number | null
  is_influencer: boolean = false
  is_following?: boolean
  instagram_username?: string
  tiktok_username?: string
  youtube_username?: string
  substack_username?: string
  /**
   * Public profile handle. Optional — users can pick one at signup or later
   * from the editor. Presence toggles URL construction from the legacy
   * `/profile/<_id>` form to `/profile/<username>` via `buildProfileHref()`.
   * Case is preserved for display; server-side uniqueness is case-insensitive.
   */
  username?: string
  website_label?: string
  website_url?: string
  book_url?: string
  referral_access_code?: string
  following_user_ids: string[]
  followers_count: number
  following_count: number
  post_count?: number
  recommendation_count?: number
  /**
   * Count of user-initiated recommendations (excludes auto-generated rows).
   * Matches `GET /api/v2/recommendations/mine?exclude_auto=true` pagination.total.
   * May be absent on older payloads — fall back to recommendation_count.
   */
  user_initiated_recommendation_count?: number
  countries_count?: number
  profile_type?: string
  /** Total audience size for the "Trusted by N travelers" profile header stat on non-own profiles. */
  total_audience?: number
  /** Safe Advisor Pro badge summary on public profiles; absent on non-advisors. */
  advisor_pro?: PublicAdvisorProSummary

  get isPremiumMember(): boolean {
    if (this.is_influencer) {
      return true
    }
    return !!this.membership
  }

  constructor(data: Record<string, unknown>) {
    this._id = data._id as string
    this.cognito_username = (data.cognito_username as string) ?? ''
    this.name = (data.name as string) ?? 'Anonymous'
    this.name = this.name.trim()
    if (this.name.length === 0) {
      this.name = 'Anonymous'
    }
    this.email = data.email as string
    this.bio = data.bio as string | undefined
    this.date_of_birth = new Date(data.date_of_birth as string)
    this.points = (data.points as number) ?? 0
    // Map avatar URLs - /api/v1/users/filtered and /api/v2/users/featured both return
    // large_avatar_url, small_avatar_url, profile_image_url, hero_image_url
    this.large_avatar_url = data.large_avatar_url as string | undefined
    this.small_avatar_url = data.small_avatar_url as string | undefined
    this.avatar_url = data.avatar_url as string | undefined
    this.profile_image_url = data.profile_image_url as string | undefined
    this.heroImage = data.heroImage as string | undefined
    this.hero_image_secondary_url = data.hero_image_secondary_url as string | undefined
    this.secondary_hero_image_url = data.secondary_hero_image_url as string | undefined
    this.cover_image = data.cover_image as string | undefined

    // Migrate old S3 bucket URLs to new bucket
    if (this.large_avatar_url) {
      this.large_avatar_url = this.large_avatar_url
        .replace(
          'globethriversmedia112710-prod.s3.eu-west-1.amazonaws.com',
          'globethriversmedia87608-prodnew.s3.eu-west-1.amazonaws.com'
        )
        .replace(
          'globethriversmedia112710-prod.s3-accelerate.amazonaws.com',
          'globethriversmedia87608-prodnew.s3.eu-west-1.amazonaws.com'
        )
    }

    if (this.small_avatar_url) {
      this.small_avatar_url = this.small_avatar_url
        .replace(
          'globethriversmedia112710-prod.s3.eu-west-1.amazonaws.com',
          'globethriversmedia87608-prodnew.s3.eu-west-1.amazonaws.com'
        )
        .replace(
          'globethriversmedia112710-prod.s3-accelerate.amazonaws.com',
          'globethriversmedia87608-prodnew.s3.eu-west-1.amazonaws.com'
        )
    }

    // Preserve actual profile_type value (including null/undefined) for admin dashboard
    // Don't default to 'regular' so we can see which users need their profile_type fixed
    this.profile_type = data.profile_type as string | undefined

    this.hero_image_url = data.hero_image_url as string | undefined
    this.city_identifying_name = data.city_identifying_name as string | undefined
    this.traveller_types = (data.traveller_types as TravellerType[]) ?? []
    this.travel_with_types = (data.travel_with_types as TravelWithType[]) ?? []
    this.city = data.city ? new City(data.city as CityData) : undefined

    if (Array.isArray(data.dream_destinations)) {
      this.dream_destinations = data.dream_destinations.map(
        (dream_destination: CityData) => {
          return new City(dream_destination)
        }
      )
    }

    this.dream_destination_reason = data.dream_destination_reason as string | undefined
    this.did_complete_onboarding = (data.did_complete_onboarding as boolean) ?? false
    this.membership = data.membership as Membership | undefined
    this.blocked_user_ids = (data.blocked_user_ids as string[]) ?? []
    this.blocked_circle_user_ids = (data.blocked_circle_user_ids as string[]) ?? []
    this.is_deleted = (data.is_deleted as boolean) ?? false
    this.is_hidden = (data.is_hidden as boolean) ?? false
    this.is_banned = (data.is_banned as boolean) ?? false
    this.is_approved = data.is_approved as boolean | undefined
    this.is_featured = data.is_featured as boolean | undefined
    this.is_gt_plus = data.is_gt_plus as boolean | undefined
    this.is_internal = data.is_internal === true
    this.hotel_margin = (data.hotel_margin as number | null) ?? null
    this.is_influencer = (data.is_influencer as boolean) ?? false
    this.is_following = (data.is_following as boolean) ?? false
    this.instagram_username = data.instagram_username as string | undefined
    this.tiktok_username = data.tiktok_username as string | undefined
    this.youtube_username = data.youtube_username as string | undefined
    this.substack_username = data.substack_username as string | undefined
    this.username = typeof data.username === 'string' && data.username.length > 0
      ? (data.username as string)
      : undefined
    this.website_label = data.website_label as string | undefined
    this.website_url = data.website_url as string | undefined
    this.book_url = data.book_url as string | undefined
    this.referral_access_code = (data.referral_access_code as string) ?? ''
    this.following_user_ids = (data.following_user_ids as string[]) ?? []
    this.followers_count = (data.followers_count as number) ?? 0
    this.following_count = this.following_user_ids.length
    this.post_count = (data.post_count as number) ?? 0
    this.recommendation_count = (data.recommendation_count as number) ?? 0
    this.user_initiated_recommendation_count = data.user_initiated_recommendation_count as number | undefined
    this.countries_count = (data.countries_count as number) ?? 0
    this.total_audience = data.total_audience as number | undefined
    this.advisor_pro = data.advisor_pro as PublicAdvisorProSummary | undefined

  }

  get displayUser(): DisplayUser {
    return new DisplayUser({
      _id: this._id,
      name: this.is_deleted ? 'Deleted User' : this.name,
      bio: this.bio,
      subtitle: this.city ? this.city.long_name : 'Globe Thriver',
      large_avatar_url: this.is_deleted ? null : this.large_avatar_url,
      small_avatar_url: this.is_deleted ? null : this.small_avatar_url,
      hero_image_url: this.hero_image_url,
      city: this.city,
      dream_destinations: this.dream_destinations,
      dream_destination_reason: this.dream_destination_reason,
      traveller_types: this.traveller_types ?? [],
      travel_with_types: this.travel_with_types ?? [],
      points: this.points,
      is_influencer: this.is_influencer,
      instagram_username: this.instagram_username,
      tiktok_username: this.tiktok_username,
      youtube_username: this.youtube_username,
      substack_username: this.substack_username,
      referral_access_code: this.referral_access_code,
      following_user_ids: this.following_user_ids,
      followers_count: this.followers_count,
      following_count: this.following_count,
      profile_type: this.profile_type,
      isPremiumMember: this.isPremiumMember
    })
  }

  filterBlockedUsersFromPosts(posts: Post[]) {
    return posts
      .filter(post => {
        return !this.blocked_user_ids?.includes(post.user?._id ?? '')
      })
      .filter(post => {
        return !this.blocked_circle_user_ids?.includes(
          post.circle_user?.id ?? ''
        )
      })
  }
}
